import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { useUser, useAuth as useClerkAuth } from '@clerk/clerk-react'
import type { AuthUser, AuthState, Profile } from '@/types'
import { isDemoMode_M12_4 } from '@/lib/constants'
import { fetchProfile_M4_1 } from '@/services/profile'
import { demoSignIn_M1_20, demoSignOut_M1_21 } from '@/services/auth'
import { checkAgeVerification_M1_3, setAgeVerified_M1_4 } from '@/services/auth'
import { demoFetchProfile_M11_7 } from '@/services/demo'
import { setPreviewMode_M13_1 } from '@/services/preview'

const PREVIEW_SESSION_KEY = 'kinkup_preview'

/** Synthetic guest user for preview mode (no backend required) */
const PREVIEW_USER: AuthUser = {
  id: 'preview_guest',
  email: 'guest@preview',
  role: 'sub',
  displayName: 'Guest',
  profile: {
    id: 'preview_guest',
    role: 'sub',
    display_name: 'Guest',
    bio: 'Preview mode — sign up to make it yours.',
    pronouns: null,
    email: 'guest@preview',
    is_switch_paid: false,
    calendar_public: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  isDemo: true,
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  ageVerified: boolean
  verifyAge: () => void
  /** True when user is in guest preview (read-only browsing) */
  isPreview: boolean
  /** Enter guest preview mode without credentials */
  startPreview: () => void
  /** Exit preview mode back to login */
  exitPreview: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider_M1_1({ children }: { children: React.ReactNode }) {
  const isDemo = isDemoMode_M12_4()

  if (isDemo) {
    return <DemoAuthProvider>{children}</DemoAuthProvider>
  }

  return <ClerkAuthProvider>{children}</ClerkAuthProvider>
}

function ClerkAuthProvider({ children }: { children: React.ReactNode }) {
  const { user: clerkUser, isLoaded: clerkLoaded, isSignedIn } = useUser()
  const { signOut: clerkSignOut } = useClerkAuth()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [ageVerified, setAgeVerified] = useState(() => checkAgeVerification_M1_3())
  const [previewUser, setPreviewUser] = useState<AuthUser | null>(null)

  // Restore preview session on mount
  useEffect(() => {
    if (sessionStorage.getItem(PREVIEW_SESSION_KEY) === '1') {
      setPreviewUser(PREVIEW_USER)
      setPreviewMode_M13_1(true)
    }
  }, [])

  useEffect(() => {
    if (!clerkUser?.id) {
      setProfile(null)
      return
    }

    fetchProfile_M4_1(clerkUser.id)
      .then((p) => {
        if (p) setProfile(p)
      })
      .catch((err) => {
        console.error('Failed to fetch profile:', err)
      })
  }, [clerkUser?.id])

  const isInPreview = !!previewUser

  const user: AuthUser | null =
    previewUser ??
    (clerkUser && profile
      ? {
          id: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress ?? '',
          role: profile.role,
          displayName: profile.display_name ?? clerkUser.firstName ?? '',
          profile,
          isDemo: false,
        }
      : null)

  const startPreview = useCallback(() => {
    sessionStorage.setItem(PREVIEW_SESSION_KEY, '1')
    setPreviewUser(PREVIEW_USER)
    setPreviewMode_M13_1(true)
  }, [])

  const exitPreview = useCallback(() => {
    sessionStorage.removeItem(PREVIEW_SESSION_KEY)
    setPreviewUser(null)
    setPreviewMode_M13_1(false)
  }, [])

  const verifyAge = useCallback(() => {
    setAgeVerified_M1_4()
    setAgeVerified(true)
  }, [])

  const signOutHandler = useCallback(async () => {
    if (isInPreview) {
      exitPreview()
      return
    }
    await clerkSignOut()
    setProfile(null)
  }, [isInPreview, exitPreview, clerkSignOut])

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      isLoaded: clerkLoaded || isInPreview,
      isSignedIn: (!!isSignedIn && !!profile) || isInPreview,
      signIn: async () => {
        // Clerk handles sign-in via its own components
      },
      signOut: signOutHandler,
      ageVerified,
      verifyAge,
      isPreview: isInPreview,
      startPreview,
      exitPreview,
    }),
    [user, clerkLoaded, isInPreview, isSignedIn, profile, signOutHandler, ageVerified, verifyAge, startPreview, exitPreview]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function DemoAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isPreview, setIsPreview] = useState(false)
  const [ageVerified, setAgeVerified] = useState(() => checkAgeVerification_M1_3())

  useEffect(() => {
    // Restore preview session
    if (sessionStorage.getItem(PREVIEW_SESSION_KEY) === '1') {
      setUser(PREVIEW_USER)
      setIsPreview(true)
      setPreviewMode_M13_1(true)
      setIsLoaded(true)
      return
    }
    // Restore normal demo session
    const stored = sessionStorage.getItem('demo_user')
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as { id: string; email: string }
        const profile = demoFetchProfile_M11_7(parsed.id)
        if (profile) {
          setUser({
            id: profile.id,
            email: profile.email ?? '',
            role: profile.role,
            displayName: profile.display_name ?? '',
            profile,
            isDemo: true,
          })
        }
      } catch {
        // ignore parse errors
      }
    }
    setIsLoaded(true)
  }, [])

  const signIn = useCallback(async (email: string, _password: string) => {
    const result = demoSignIn_M1_20({ email, password: _password })
    if (!result) throw new Error('Invalid demo credentials')

    sessionStorage.setItem('demo_user', JSON.stringify({ id: result.id, email: result.email }))
    setUser(result)
  }, [])

  const signOut = useCallback(async () => {
    demoSignOut_M1_21()
    sessionStorage.removeItem('demo_user')
    sessionStorage.removeItem(PREVIEW_SESSION_KEY)
    setUser(null)
    setIsPreview(false)
    setPreviewMode_M13_1(false)
  }, [])

  const startPreview = useCallback(() => {
    sessionStorage.setItem(PREVIEW_SESSION_KEY, '1')
    setUser(PREVIEW_USER)
    setIsPreview(true)
    setPreviewMode_M13_1(true)
  }, [])

  const exitPreview = useCallback(() => {
    sessionStorage.removeItem(PREVIEW_SESSION_KEY)
    setUser(null)
    setIsPreview(false)
    setPreviewMode_M13_1(false)
  }, [])

  const verifyAge = useCallback(() => {
    setAgeVerified_M1_4()
    setAgeVerified(true)
  }, [])

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      isLoaded,
      isSignedIn: !!user,
      signIn,
      signOut,
      isPreview,
      startPreview,
      exitPreview,
      ageVerified,
      verifyAge,
    }),
    [user, isLoaded, signIn, signOut, isPreview, startPreview, exitPreview, ageVerified, verifyAge]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth_M1_2() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth_M1_2 must be used within AuthProvider_M1_1')
  return ctx
}
